import { io, type Socket } from 'socket.io-client';
import { config } from '@/config';

/** Même origine que la SPA si `VITE_SOCKET_URL` est vide (prod derrière nginx sur le même host). */
function resolveSocketBaseUrl(): string {
  const fromEnv = (config.SOCKET_URL ?? '').trim();
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}

export const socket: Socket = io(resolveSocketBaseUrl(), {
  autoConnect: false,
  reconnection: true,
  reconnectionAttempts: 10,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 10000,
  timeout: 20000,
  // Polling d’abord : derrière le proxy Vite (dev), l’upgrade WebSocket échoue souvent ; le polling HTTP /socket.io fonctionne.
  transports: ['polling', 'websocket'],
});

let lifecycleListenersBound = false;

function bindLifecycleListeners(): void {
  if (lifecycleListenersBound) return;

  socket.on('connect', () => {
    console.info('[Socket] Connected:', socket.id);
  });

  socket.on('connect_error', (err) => {
    // 502 / xhr poll error = souvent indispo temporaire du backend ou du proxy (nginx) — pas une erreur applicative du bloc évaluation.
    console.warn('[Socket] Connexion impossible:', err.message);
  });

  socket.on('disconnect', (reason) => {
    console.info('[Socket] Disconnected:', reason);
    if (reason === 'io server disconnect') {
      // Server initiated the disconnect — reconnect manually
      socket.connect();
    }
  });

  lifecycleListenersBound = true;
}

/**
 * Connect to the socket server with an auth token.
 */
export function connectSocket(token: string): void {
  bindLifecycleListeners();
  if (socket.connected || socket.active) return;

  socket.auth = { token };
  socket.connect();
}

/**
 * Disconnect from the socket server.
 */
export function disconnectSocket(): void {
  if (!socket.connected && !socket.active) return;
  socket.disconnect();
}

/**
 * Listen to a typed socket event. Returns an unsubscribe function.
 */
export function onEvent<T>(event: string, callback: (data: T) => void): () => void {
  socket.on(event, callback as (...args: unknown[]) => void);
  return () => {
    socket.off(event, callback as (...args: unknown[]) => void);
  };
}

/**
 * Emit a typed socket event.
 */
export function emitEvent(event: string, data?: unknown): void {
  socket.emit(event, data);
}
